import {
  Badge,
  Box,
  Card,
  Group,
  Paper,
  Progress,
  SimpleGrid,
  Stack,
  Table,
  Text,
  useComputedColorScheme,
} from "@mantine/core";
import { Printable, usePrintableContext } from "@components";

import { useTranslation } from "react-i18next";
import DyscalculiaTaskCard, {
  accuracyColor,
  formatSeconds,
  getStats,
  resolveTaskLabelKey,
} from "@components/testContainer/common/DyscalculiaTaskCard";
import {
  DyscalculiaMetrics,
  DyscalculiaInconclusiveReason,
  DyscalculiaRiskClassification,
  DyscalculiaScore,
  DyscalculiaTaskResult,
  DyscalculiaTaskType,
  DyscalculiaTestResult,
} from "types/testTypes";
import {
  DYSCALCULIA_DIFFICULTY_LEVEL_PRESENTATION,
  DYSCALCULIA_DIFFICULTY_LEVELS,
  DYSCALCULIA_DIFFICULTY_STATUS_PRESENTATION,
} from "@constants/testConstants/testSpecificConstants/dyscalculiaDifficultyConstants";

const TASK_DESCRIPTIONS: Record<DyscalculiaTaskType, string> = {
  magnitudeComparisonSymbolic:
    "Assesses speed and accuracy comparing symbolic numbers to gauge magnitude understanding.",
  magnitudeComparisonNonSymbolic:
    "Measures non-symbolic magnitude sense by judging which dot set is larger without counting.",
  stroop:
    "Tests number-size interference to reveal automatic processing versus inhibitory control.",
  dotEnumeration: "Checks rapid dot counting to probe subitizing and working estimation skills.",
  numberLine: "Assesses accuracy and speed in estimating relative line position as a percentage.",
};

const getTaskDescription = (task: DyscalculiaTaskResult) => {
  if (task.taskType === "magnitudeComparisonSymbolic") {
    if (task.taskId?.includes("fraction_compare")) {
      return "Assesses symbolic magnitude comparison using fractions and decimals to probe proportional reasoning.";
    }

    if (task.taskId?.includes("negative")) {
      return "Measures signed-number ordering by comparing negative values under time pressure.";
    }
  }

  return TASK_DESCRIPTIONS[task.taskType] ?? "Task measures numerical processing efficiency.";
};

const CLASSIFICATION_PRESENTATION: Record<
  DyscalculiaRiskClassification,
  { label: string; compactLabel: string; color: string }
> = {
  lowRisk: { label: "Within expected range", compactLabel: "Expected", color: "green" },
  atRisk: { label: "Monitor", compactLabel: "Monitor", color: "yellow" },
  highRisk: { label: "Follow-up Recommended", compactLabel: "Follow-up", color: "red" },
};

const NOT_SCORED_PRESENTATION = {
  label: "Not scored",
  compactLabel: "No data",
  color: "gray",
};

const INCONCLUSIVE_PRESENTATION = {
  label: "Inconclusive",
  compactLabel: "Inconclusive",
  color: "gray",
};

const INCONCLUSIVE_REASON_COPY: Record<DyscalculiaInconclusiveReason, string> = {
  insufficientItems: "Too few valid answers were recorded to settle the result.",
  insufficientDifficultyCoverage:
    "Harder questions were not administered, so strong performance on easier questions cannot settle the result.",
  lowConfidence: "The response pattern did not separate the risk categories confidently enough.",
};

const toPercent = (share: number) => Math.min(100, Math.max(0, share * 100));

const weightedAccuracyPercent = (score: DyscalculiaScore) => toPercent(score.abilityEstimate ?? 0);

const formatAbility = (abilityEstimate: number | null) =>
  abilityEstimate === null ? "—" : `${Math.round(toPercent(abilityEstimate))}%`;

const formatConfidence = (confidence: number | null) =>
  confidence === null ? "—" : `${Math.round(confidence * 100)}%`;

const scorePresentation = (score: DyscalculiaScore) =>
  score.isClassificationConclusive && score.classification
    ? CLASSIFICATION_PRESENTATION[score.classification]
    : INCONCLUSIVE_PRESENTATION;

function DomainProfile({
  metrics,
  taskResults,
}: {
  metrics: DyscalculiaMetrics;
  taskResults: DyscalculiaTaskResult[];
}) {
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme("light");
  const badgeVariant = colorScheme === "dark" ? "light" : "filled";

  return (
    <Table.ScrollContainer minWidth={420} type="native">
      <Table verticalSpacing="xs" horizontalSpacing="xs" withRowBorders={false}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Task</Table.Th>
            <Table.Th>Weighted accuracy</Table.Th>
            <Table.Th>Items</Table.Th>
            <Table.Th>Speed</Table.Th>
            <Table.Th>Indication</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {metrics.tasks.map((score) => {
            const task = taskResults.find(
              (candidate) =>
                candidate.taskType === score.taskType && candidate.taskId === score.taskId,
            );
            const labelKey = task ? resolveTaskLabelKey(task) : null;
            const indication = scorePresentation(score);
            const meanReactionTime = task ? getStats(task.results).avgRt : null;

            return (
              <Table.Tr key={`${score.taskType}-${score.taskId ?? ""}`}>
                <Table.Td>
                  <Text size="sm">{labelKey ? t(labelKey) : score.taskType}</Text>
                </Table.Td>
                <Table.Td>
                  <Group gap={6} wrap="nowrap">
                    <Progress
                      value={weightedAccuracyPercent(score)}
                      color={indication.color}
                      size="sm"
                      radius="xl"
                      w={56}
                    />
                    <Text size="sm">{formatAbility(score.abilityEstimate)}</Text>
                  </Group>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{score.itemsWithResolvedLevel}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{formatSeconds(meanReactionTime)}</Text>
                </Table.Td>
                <Table.Td style={{ whiteSpace: "nowrap" }}>
                  <Badge
                    size="sm"
                    variant={badgeVariant}
                    color={indication.color}
                    styles={{ label: { overflow: "visible" } }}
                  >
                    {indication.compactLabel}
                  </Badge>
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

const findTaskScore = (metrics: DyscalculiaMetrics | undefined, task: DyscalculiaTaskResult) =>
  metrics?.tasks.find(
    (scored) => scored.taskType === task.taskType && scored.taskId === task.taskId,
  );

function DifficultyProfile({ metrics }: { metrics: DyscalculiaMetrics }) {
  const { levelAccuracyProfile, itemsAnswered, itemsWithResolvedLevel } = metrics;
  if (levelAccuracyProfile.length === 0) return null;

  const skipped = itemsAnswered - itemsWithResolvedLevel;

  return (
    <Stack gap={6} w="100%">
      <Text size="xs" fw={600}>
        Difficulty performance
      </Text>
      <SimpleGrid type="container" cols={{ base: 1, "320px": 2 }} spacing={6}>
        {DYSCALCULIA_DIFFICULTY_LEVELS.map((level) => {
          const entry = levelAccuracyProfile.find((candidate) => candidate.level === level);
          const presentation = DYSCALCULIA_DIFFICULTY_LEVEL_PRESENTATION[level];

          return (
            <Paper key={level} p="xs" radius="sm" withBorder opacity={entry ? 1 : 0.58}>
              <Stack gap={5}>
                <Group gap={6} align="flex-start" wrap="nowrap">
                  <Badge
                    size="xs"
                    variant={entry ? "filled" : "outline"}
                    color={entry ? "blue" : "gray"}
                  >
                    L{level}
                  </Badge>
                  <Text size="xs" fw={600}>
                    {presentation.title}
                  </Text>
                </Group>
                <Text size="xs" c="dimmed">
                  {entry ? `${entry.itemsCorrect}/${entry.itemsAnswered} correct` : "Not given"}
                </Text>
                {entry && (
                  <Progress
                    value={entry.accuracy * 100}
                    color={accuracyColor(entry.accuracy * 100)}
                    size="sm"
                    radius="xl"
                  />
                )}
              </Stack>
            </Paper>
          );
        })}
      </SimpleGrid>
      {skipped > 0 && (
        <Text size="xs" c="dimmed">
          {skipped} of {itemsAnswered} answers had no known difficulty and were left out.
        </Text>
      )}
    </Stack>
  );
}

function DifficultyLegend() {
  return (
    <Paper p="md" radius="md" bg="var(--mantine-color-default-hover)">
      <SimpleGrid type="container" cols={{ base: 1, "720px": 2 }} spacing="lg">
        <Stack gap={8}>
          <Text size="xs" fw={700} c="dimmed" tt="uppercase" lts={0.5}>
            Difficulty
          </Text>
          <Group gap="md" wrap="wrap">
            {DYSCALCULIA_DIFFICULTY_LEVELS.map((level) => (
              <Group key={level} gap={4} wrap="nowrap">
                <Text size="xs" fw={700} c="blue">
                  L{level}
                </Text>
                <Text size="xs" fw={500}>
                  {DYSCALCULIA_DIFFICULTY_LEVEL_PRESENTATION[level].title}
                </Text>
              </Group>
            ))}
          </Group>
        </Stack>

        <Stack gap={8}>
          <Text size="xs" fw={700} c="dimmed" tt="uppercase" lts={0.5}>
            Performance at each level
          </Text>
          <Group gap="md" wrap="wrap">
            {Object.entries(DYSCALCULIA_DIFFICULTY_STATUS_PRESENTATION).map(
              ([status, presentation]) => (
                <Group key={status} gap={6} wrap="nowrap">
                  <Box
                    w={9}
                    h={9}
                    bg={`var(--mantine-color-${presentation.color}-6)`}
                    style={{ borderRadius: "50%", flexShrink: 0 }}
                  />
                  <Text size="xs" fw={500}>
                    {presentation.label}
                  </Text>
                </Group>
              ),
            )}
          </Group>
        </Stack>
      </SimpleGrid>
    </Paper>
  );
}

interface DyscalculiaTestEntryProps {
  entry: DyscalculiaTestResult | null;
  header?: React.ReactNode;
}

function DyscalculiaTestEntry({ entry, header }: DyscalculiaTestEntryProps) {
  const taskResults = entry?.taskResults ?? [];
  const colorScheme = useComputedColorScheme("light");
  const { prettier } = usePrintableContext();

  if (!Array.isArray(taskResults) || taskResults.length === 0) {
    return (
      <Printable.Section>
        <Stack>
          {header}
          <Text size="sm" c="dimmed">
            No dyscalculia results available.
          </Text>
        </Stack>
      </Printable.Section>
    );
  }

  const metrics = entry?.metrics ?? null;
  const risk = metrics ? scorePresentation(metrics) : NOT_SCORED_PRESENTATION;
  const preliminarySignal =
    metrics && !metrics.isClassificationConclusive && metrics.classification
      ? CLASSIFICATION_PRESENTATION[metrics.classification]
      : null;
  const riskBg =
    colorScheme === "dark"
      ? `color-mix(in srgb, transparent, var(--mantine-color-${risk.color}-6) 15%)`
      : `var(--mantine-color-${risk.color}-1)`;
  const riskText = `var(--mantine-color-${risk.color}-${colorScheme === "dark" ? 3 : 9})`;

  return (
    <>
      <Printable.Section>
        <Stack gap="md">
          {header}
          <Box mx={prettier ? 0 : "xl"}>
            <DifficultyLegend />
          </Box>
          <Box mx={prettier ? 0 : "xl"}>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {taskResults.map((task) => (
                <DyscalculiaTaskCard
                  key={task.taskId ? `${task.taskType}-${task.taskId}` : task.taskType}
                  task={task}
                  description={getTaskDescription(task)}
                  trialLevels={findTaskScore(entry?.metrics, task)?.trialLevels}
                  showTrials
                />
              ))}
            </SimpleGrid>
          </Box>
        </Stack>
      </Printable.Section>
      <Printable.Section>
        <Box mx={prettier ? 0 : "xl"}>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb={"md"} className="print-cols-2">
            <Card withBorder radius="md" p="md" shadow="xs">
              {metrics ? (
                <Stack gap="sm">
                  <Text fw={600}>Profile by task</Text>
                  <Text size="sm" c="dimmed">
                    Difficulty-adjusted performance by numerical task.
                  </Text>
                  <DomainProfile metrics={metrics} taskResults={taskResults} />
                </Stack>
              ) : (
                <Stack gap="sm">
                  <Text fw={600}>Not scored by difficulty</Text>
                  <Text size="sm" c="dimmed">
                    No task in this session carries a difficulty ladder, so there is nothing to
                    weight the answers by. Per-task accuracy is shown above and is not comparable
                    between participants who were given different items.
                  </Text>
                </Stack>
              )}
            </Card>

            <Card withBorder radius="md" p="md" shadow="xs">
              <Stack gap="sm" align="center">
                <Group gap="sm" justify="flex-start" w={"100%"}>
                  <Text fw={600}>Dyscalculia screening</Text>
                  <Badge variant="filled" color={risk.color}>
                    {risk.label}
                  </Badge>
                </Group>
                {(!metrics || !metrics.isClassificationConclusive) && (
                  <Text size="sm" c="dimmed">
                    {metrics
                      ? INCONCLUSIVE_REASON_COPY[metrics.inconclusiveReason ?? "lowConfidence"]
                      : "No weighted result is available because the session has no difficulty data."}
                  </Text>
                )}
                {preliminarySignal && (
                  <Paper px="sm" py={8} radius="sm" bg={riskBg} c={riskText} w="100%">
                    <Text size="xs" fw={600}>
                      Preliminary pattern only
                    </Text>
                    <Text size="sm">
                      Responses currently lean toward “{preliminarySignal.label}”, but this is not a
                      settled indication.
                    </Text>
                  </Paper>
                )}
                <Paper
                  px="sm"
                  py={8}
                  radius="sm"
                  bg={riskBg}
                  c={riskText}
                  withBorder={false}
                  w="100%"
                >
                  <Text size="sm">
                    {metrics?.abilityEstimate == null
                      ? "No answer in this session carried a difficulty the backend could read."
                      : `${formatAbility(metrics.abilityEstimate)} difficulty-weighted accuracy across ${metrics.levelAccuracyProfile.length} levels given · ${formatConfidence(metrics.classificationConfidence)} confidence`}
                  </Text>
                </Paper>
                {metrics && <DifficultyProfile metrics={metrics} />}
              </Stack>
            </Card>
          </SimpleGrid>
        </Box>
      </Printable.Section>
    </>
  );
}

DyscalculiaTestEntry.selfSections = true;

export default DyscalculiaTestEntry;
